import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, BadgeCheck, ChevronLeft, ChevronRight, Map as MapIcon, X, Store, ShoppingBag } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CATEGORIES } from "@/data/products";

function BranchSection({ b, i }) {
  const [idx, setIdx] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);
  const photos = b.photos?.length ? b.photos : [CATEGORIES[i % CATEGORIES.length].img];
  const flip = i % 2 === 1;
  const hasMap = !!(b.map_embed_url || (b.latitude && b.longitude));
  const embedUrl = b.map_embed_url || (hasMap ? `https://maps.google.com/maps?q=${b.latitude},${b.longitude}&z=15&output=embed` : null);
  const shareUrl = b.map_share_url || (hasMap ? `https://www.google.com/maps/search/?api=1&query=${b.latitude},${b.longitude}` : null);

  return (
    <motion.section
      data-testid={`branch-card-${b.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`grid items-center gap-8 border-b border-champagne/40 py-12 last:border-b-0 md:grid-cols-12 ${flip ? "md:[direction:rtl]" : ""}`}
    >
      <div className="md:col-span-7 md:[direction:ltr]">
        <div className="group relative overflow-hidden rounded-3xl border border-champagne/40" data-testid={`branch-photos-${b.id}`}>
          <img src={photos[idx]} alt={b.name} className="aspect-[16/10] w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
          {photos.length > 1 && (
            <>
              <button data-testid={`branch-photo-prev-${b.id}`} onClick={() => setIdx((idx - 1 + photos.length) % photos.length)} className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-cream backdrop-blur transition-opacity md:opacity-0 md:group-hover:opacity-100" aria-label="Previous photo">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button data-testid={`branch-photo-next-${b.id}`} onClick={() => setIdx((idx + 1) % photos.length)} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-cream backdrop-blur transition-opacity md:opacity-0 md:group-hover:opacity-100" aria-label="Next photo">
                <ChevronRight className="h-4 w-4" />
              </button>
              <span className="absolute bottom-3 right-3 rounded-full bg-cream/85 px-3 py-1 font-mono text-[9px] font-bold tracking-widest text-forest">{idx + 1} / {photos.length}</span>
            </>
          )}
          <span className="absolute left-4 top-4 rounded-full bg-gold px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-forest">{b.code}</span>
        </div>
      </div>

      <div className="md:col-span-5 md:[direction:ltr]">
        <span className="font-display text-6xl italic text-stroke-gold sm:text-7xl">0{i + 1}</span>
        <h2 className="mt-3 font-display text-3xl tracking-tight text-cream sm:text-4xl" data-testid="branch-name">{b.name}</h2>

        {b.address && (
          <p className="mt-4 flex items-start gap-3 text-sm leading-relaxed text-sage" data-testid="contact-address">
            <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold" />
            <span>{[b.address.line1, b.address.line2, [b.address.city, b.address.state].filter(Boolean).join(", "), [b.address.pincode, b.address.country].filter(Boolean).join(" · ")].filter(Boolean).join(", ")}</span>
          </p>
        )}

        <div className="mt-4 space-y-2.5 text-sm">
          {b.phone && (
            <a data-testid="branch-phone" href={`tel:${b.phone}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
              <Phone className="h-4 w-4 text-gold" /> {b.phone}
            </a>
          )}
          {b.alternate_phone && (
            <a data-testid="branch-alt-phone" href={`tel:${b.alternate_phone}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
              <Phone className="h-4 w-4 text-gold" /> {b.alternate_phone}
            </a>
          )}
          {b.email && (
            <a data-testid="branch-email" href={`mailto:${b.email}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
              <Mail className="h-4 w-4 text-gold" /> {b.email}
            </a>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {b.gstin && (
            <span className="flex items-center gap-1.5 rounded-full bg-moss px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-gold">
              <BadgeCheck className="h-3 w-3" /> GSTIN {b.gstin}
            </span>
          )}
          {b.ecommerce_enabled && (
            <span className="flex items-center gap-1.5 rounded-full bg-moss px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-cream">
              <ShoppingBag className="h-3 w-3 text-gold" /> Ships online
            </span>
          )}
        </div>

        {hasMap && (
          <div className="mt-6 flex flex-wrap gap-3">
            <a data-testid="branch-maps-link" href={shareUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-forest transition-transform hover:scale-105">
              <MapPin className="h-3.5 w-3.5" /> View on Google Maps
            </a>
            <button data-testid={`branch-map-toggle-${b.id}`} onClick={() => setMapOpen((o) => !o)} className="flex items-center gap-2 rounded-full border border-champagne/50 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-cream transition-colors hover:border-gold hover:text-gold">
              {mapOpen ? <X className="h-3.5 w-3.5" /> : <MapIcon className="h-3.5 w-3.5" />} {mapOpen ? "Hide map" : "Show map"}
            </button>
          </div>
        )}

        {mapOpen && embedUrl && (
          <div className="mt-5 overflow-hidden rounded-2xl border border-champagne/40" data-testid="branch-map-embed">
            <iframe title={`${b.name} map`} src={embedUrl} className="h-64 w-full" loading="lazy" />
          </div>
        )}
      </div>
    </motion.section>
  );
}

export default function BranchesPage() {
  const [branches, setBranches] = useState(null);

  useSeo({
    title: "Our Branches — Visit a Soonyoung Cosmetic Showroom Near You",
    description: "Find Soonyoung cosmetic showrooms across India — store addresses, phone numbers, maps and photos. Shop original beauty products in-store and online with free delivery over ₹999.",
    path: "/stores",
  });

  useEffect(() => {
    api.get("/ecom/branches").then((r) => setBranches(r.data?.items || [])).catch(() => setBranches([]));
  }, []);

  return (
    <div data-testid="stores-page" className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 sm:pt-32">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Visit the Sanctuary</p>
            <h1 className="mt-3 font-display text-3xl tracking-tight text-cream sm:text-5xl">
              Our <span className="italic text-gold">Branches</span>
            </h1>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-fern">
            Step into the showroom — every shelf in person, every ritual with a specialist.
          </p>
        </div>
      </Reveal>

      {!branches ? (
        <div className="mt-12 space-y-10">{[...Array(2)].map((_, i) => <div key={i} className="aspect-[16/8] animate-pulse rounded-3xl bg-moss" />)}</div>
      ) : branches.length === 0 ? (
        <div className="mt-12 flex flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-24 text-center" data-testid="stores-empty">
          <Store className="h-10 w-10 text-gold" />
          <p className="mt-4 font-display text-2xl text-cream">New stores coming soon</p>
          <p className="mt-2 max-w-sm text-sm text-sage">We're planting showrooms across India — shop online meanwhile.</p>
        </div>
      ) : (
        <div className="mt-6" data-testid="stores-grid">
          {branches.map((b, i) => <BranchSection key={b.id} b={b} i={i} />)}
        </div>
      )}
    </div>
  );
}
